import { Component, OnInit } from '@angular/core';
import {
  OrganizationType,
  ORGANIZATION_TYPE_OPTIONS,
  getOrganizationTypeLabel,
} from 'src/app/core/models/organization.model';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NgxMaskDirective } from 'ngx-mask';
import { toNationalDigits } from 'src/app/shared/utils/format.utils';
import { UsStateSelect } from '../../shared/components/app-us-state-select/app-us-state-select';

interface Organization {
  id: string;
  name: string;
  organizationType: OrganizationType;
  ownerProfileId: string;
  ein?: string;
  ssn?: string;
  streetAddressLine1: string;
  streetAddressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  email?: string;
  phone?: string;
  parentOrganizationId?: string;
  billingResponsibility: string;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-organization-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective, UsStateSelect],
  templateUrl: './organization-form.component.html',
  styleUrl: './organization-form.component.scss'
})
export class OrganizationFormComponent implements OnInit {
  organizationForm: FormGroup;
  loading = false;
  loadingOrganization = false;
  errorMessage = '';
  successMessage = '';

  isEditMode = false;
  organizationId: string | null = null;

  // Dropdown options
  organizationTypes = ORGANIZATION_TYPE_OPTIONS;

  billingOptions = [
    { value: 'self', label: 'Organization pays for itself' },
    { value: 'parent', label: 'Parent organization pays' }
  ];

  // Organization types that require EIN (per database constraint)
  einRequiredTypes = [OrganizationType.Llc, OrganizationType.SCorp, OrganizationType.CCorp, OrganizationType.Partnership];
  // Organization types that require SSN (per database constraint)
  ssnRequiredTypes = [OrganizationType.Individual];
  // Organization types that show EIN field (optional for some)
  einVisibleTypes = [OrganizationType.Llc, OrganizationType.SCorp, OrganizationType.CCorp, OrganizationType.Partnership, OrganizationType.NonProfit];
  // Organization types that show SSN field (optional for some)
  ssnVisibleTypes = [OrganizationType.Individual, OrganizationType.SoleProprietorship];

  // For parent organization dropdown
  parentOrganizations: Organization[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {
    this.organizationForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      organizationType: [OrganizationType.Llc, Validators.required],
      ein: [''],
      ssn: [''],
      streetAddressLine1: ['', Validators.required],
      streetAddressLine2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
      country: ['USA', Validators.required],
      email: ['', [Validators.email]],
      phone: ['', [Validators.pattern(/^\+?[\d\s\-()]+$/)]],
      billingResponsibility: ['self', Validators.required],
      parentOrganizationId: ['']
    });
  }

  ngOnInit(): void {
    // Check if we're in edit mode
    this.organizationId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.organizationId && this.organizationId !== 'create';

    // Listen for organization type changes to update EIN/SSN validation
    this.organizationForm.get('organizationType')?.valueChanges.subscribe(type => {
      this.updateTaxIdValidators(type);
    });

    // Set initial validation based on default type
    this.updateTaxIdValidators(this.organizationForm.get('organizationType')?.value);

    if (this.isEditMode) {
      this.loadOrganization();
    }
  }

  /**
   * Update EIN/SSN validators based on organization type
   */
  private updateTaxIdValidators(orgType: OrganizationType): void {
    const einControl = this.organizationForm.get('ein');
    const ssnControl = this.organizationForm.get('ssn');

    // Clear existing validators
    einControl?.clearValidators();
    ssnControl?.clearValidators();

    // EIN validation: required for business entities, pattern always applies
    if (this.einRequiredTypes.includes(orgType)) {
      einControl?.setValidators([Validators.required, Validators.pattern(/^\d{2}-?\d{7}$/)]);
    } else if (this.einVisibleTypes.includes(orgType)) {
      einControl?.setValidators([Validators.pattern(/^\d{2}-?\d{7}$/)]);
    }

    // SSN validation: required for individuals, pattern always applies
    if (this.ssnRequiredTypes.includes(orgType)) {
      ssnControl?.setValidators([Validators.required, Validators.pattern(/^\d{3}-?\d{2}-?\d{4}$/)]);
    } else if (this.ssnVisibleTypes.includes(orgType)) {
      ssnControl?.setValidators([Validators.pattern(/^\d{3}-?\d{2}-?\d{4}$/)]);
    }

    // Update validity state
    einControl?.updateValueAndValidity();
    ssnControl?.updateValueAndValidity();
  }

  // Computed properties for template
  get showEinField(): boolean {
    const orgType = this.organizationForm.get('organizationType')?.value;
    return this.einVisibleTypes.includes(orgType);
  }

  get showSsnField(): boolean {
    const orgType = this.organizationForm.get('organizationType')?.value;
    return this.ssnVisibleTypes.includes(orgType);
  }

  get einRequired(): boolean {
    const orgType = this.organizationForm.get('organizationType')?.value;
    return this.einRequiredTypes.includes(orgType);
  }

  get ssnRequired(): boolean {
    const orgType = this.organizationForm.get('organizationType')?.value;
    return this.ssnRequiredTypes.includes(orgType);
  }

  async loadOrganization(): Promise<void> {
    this.loadingOrganization = true;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: Organization }>(
          `${environment.apiUrl}/admin/organizations/${this.organizationId}`
        )
      );

      if (response.success && response.data) {
        const org = response.data;

        // Patch form values
        this.organizationForm.patchValue({
          name: org.name,
          organizationType: org.organizationType,
          ein: org.ein || '',
          ssn: org.ssn || '',
          streetAddressLine1: org.streetAddressLine1,
          streetAddressLine2: org.streetAddressLine2 || '',
          city: org.city,
          state: org.state,
          zipCode: org.zipCode,
          country: org.country,
          email: org.email || '',
          phone: toNationalDigits(org.phone),
          billingResponsibility: org.billingResponsibility
        });

        // Update validators after patching organization type
        this.updateTaxIdValidators(org.organizationType);
      }
    } catch (error) {
      console.error('Failed to load organization:', error);
      this.errorMessage = 'Failed to load organization details';
    } finally {
      this.loadingOrganization = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.organizationForm.invalid) {
      this.organizationForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const formData = this.organizationForm.value;

      if (this.isEditMode) {
        // Update existing organization
        await firstValueFrom(
          this.http.put(
            `${environment.apiUrl}/admin/organizations/${this.organizationId}`,
            formData
          )
        );

        this.successMessage = 'Organization updated successfully';

        // Navigate back to detail view after short delay
        setTimeout(() => {
          this.router.navigate(['/organizations', this.organizationId]);
        }, 1500);
      } else {
        // Create new organization
        const response = await firstValueFrom(
          this.http.post<{ success: boolean; data: { id: string } }>(
            `${environment.apiUrl}/organizations`,
            formData
          )
        );

        // Navigate to success page
        this.router.navigate(['/success'], {
          state: {
            entities: [
              { type: 'organization', action: 'created', name: formData.name },
            ],
            message: 'Organization created successfully',
            details: {
              'Type': getOrganizationTypeLabel(formData.organizationType),
              'Location': `${formData.city}, ${formData.state}`,
              'Billing': formData.billingResponsibility === 'organization' ? 'Organization' : 'Individual'
            },
            createAnotherRoute: '/organizations/create'
          }
        });
      }
    } catch (error: any) {
      console.error('Save organization error:', error);
      this.errorMessage = this.isEditMode
        ? 'Failed to update organization. Please try again.'
        : 'Failed to create organization. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    if (this.isEditMode) {
      this.router.navigate(['/organizations', this.organizationId]);
    } else {
      this.router.navigate(['/organizations']);
    }
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Organization' : 'Create Organization';
  }

  get submitButtonText(): string {
    if (this.loading) {
      return this.isEditMode ? 'Saving...' : 'Creating...';
    }
    return this.isEditMode ? 'Save Changes' : 'Create Organization';
  }
}
